import { useContext } from 'react'
import PropTypes from 'prop-types'
import { LockClosedIcon } from '@heroicons/react/24/solid'
import Picture from '../helpers/Picture'
import { MarkupContext } from '../../providers/Markup'
import MetaBadge from '../helpers/MetaBadge'
import { formatDate } from '../../helpers/util'
import { hasRenderableText } from '../../helpers/markup'
import { getFinalBreadcrumbLabel } from '../../helpers/breadcrumbs'

const titleClassName =
  'font-highlight text-3xl font-bold leading-tight text-white md:text-4xl [&_em]:text-[#c5e84e] [&_strong]:text-[#c5e84e]'

/**
 * Full-width page header: navy content column and optional image with rounded inner edge.
 * Data comes from section header composition plus page-level tags and publication-style date.
 */
function SectionHeader(props) {
  const { createMarkup } = useContext(MarkupContext)
  const primaryTag = props.tags?.length ? props.tags[0] : null
  // Show the pill whenever there is tag/reading-time content or the page is locked.
  // (Previously gated on readingTime/isLockedContent only, which hid the pill and date
  // on unlocked articles that have a tag + publication date but no reading time.)
  const showBadge = !!(primaryTag || props.readingTime || props.isLockedContent)
  const showMetaRow = !!(showBadge || props.publicationDate)
  const hasImage = !!(props.desktopImage?.src || props.mobileImage?.src)
  const sectionTitle = hasRenderableText(props.title) ? props.title : null
  const breadcrumbTitle = sectionTitle ? null : getFinalBreadcrumbLabel(props.breadcrumbs)

  return (
    <section
      data-type="sectionHeader"
      data-index={props.idx}
      // Sharp corners on all four sides when there is no image; the rounded
      // bottom-right is part of the content+image split design only.
      className={`w-full ${hasImage ? 'lg:pr-3' : ''} ${props.outerClass || ''}`}
    >
      <div className="flex flex-col lg:flex-row lg:min-h-[280px] bg-(--sb-secondary-bg)">
        <div
          className={`flex max-w-3xl  flex-col justify-center gap-6 px-6 py-10 md:px-12 lg:py-14  ${
            hasImage ? 'lg:w-[calc(50%+1.5rem)] lg:flex-none lg:pr-10 lg:ml-auto' : 'items-start mx-auto'
          }`}
        >
          <div className={`${hasImage ? 'mr-auto' : 'mx-auto'} w-fit max-w-full text-left`}>
            {showMetaRow ? (
              <div className={`flex flex-wrap items-center gap-4 mb-4`}>
                {showBadge ? (
                  <MetaBadge
                    primaryText={primaryTag}
                    secondaryText={props.readingTime}
                    icon={props.isLockedContent ? <LockClosedIcon className="h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
                    tagType={props.isLockedContent ? 'locked_news_articles' : 'unlocked_news_articles'}
                  />
                ) : null}
                {props.publicationDate ? (
                  <time className="text-sm font-normal text-white!" dateTime={props.publicationDate}>
                    {formatDate(props.publicationDate)}
                  </time>
                ) : null}
              </div>
            ) : null}

            {sectionTitle ? (
              <div
                className={titleClassName}
                data-rte="true"
                dangerouslySetInnerHTML={createMarkup(sectionTitle)}
              />
            ) : breadcrumbTitle ? (
              <h1 className={titleClassName}>{breadcrumbTitle}</h1>
            ) : null}
          </div>
        </div>

        {hasImage ? (
          // 50:50 split with the content (both flex-1). 12px gap on the right
          // (lg:pr-3) so the image stops short of the screen edge. breakpoint=1024
          // switches to the mobile image while the layout is still stacked (below lg),
          // i.e. on tablet/iPad.
          <div className="hidden md:!block relative min-h-[200px] w-full lg:w-1/2 lg:flex-none lg:-ml-6 overflow-hidden md:rounded-3xl md:rounded-tr-none">
            <Picture
              desktopImage={props.desktopImage}
              mobileImage={props.mobileImage}
              alt={primaryTag || sectionTitle || breadcrumbTitle || 'Section header image'}
              breakpoint={1024}
              // Positioned against the relative container (which gets its height
              // from the flex row / min-h), NOT the inline <picture> box — using
              // h-full here would collapse to 0 because <picture> has no height.
              className="absolute inset-0 h-full w-full object-cover"
            />
          </div>
        ) : null}
      </div>
    </section>
  )
}

SectionHeader.propTypes = {
  idx: PropTypes.number,
  title: PropTypes.string,
  desktopImage: PropTypes.object,
  mobileImage: PropTypes.object,
  readingTime: PropTypes.string,
  publicationDate: PropTypes.string,
  tags: PropTypes.arrayOf(PropTypes.string),
  isLockedContent: PropTypes.bool,
  breadcrumbs: PropTypes.arrayOf(PropTypes.object),
}

export default SectionHeader
